import { BambooHrIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/bamboohr';
import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { lightMdToJsx } from '../../../../../../services/formatter';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  MASK,
  masked
} from '../common';
import { removeBambooHrConfig, useBambooHrConfig } from './service';

const NAME = 'BambooHR';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const SetupDialog = ({
  config,
  open,
  onClose,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<BambooHrIntegrationConfig>) => {
  const { t } = useTranslation();

  const { subdomain, apiKey } = config?.data || { subdomain: '', apiKey: '' };

  const [state, setState] = useState<{
    subdomain: string;
    apiKey: string;
  }>({
    subdomain,
    apiKey: apiKey ? MASK : ''
  });
  useEffect(() => {
    setState({ subdomain, apiKey: masked(apiKey) });
  }, [subdomain, apiKey]);

  if (!config) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('org.integrations.bambooHr.setupInfo.p1')}
        </Alert>

        <FormRow>
          <CustomInputLabel
            label={t('org.integrations.common.subdomain')}
            fullWidth
          >
            <TextField
              variant="outlined"
              value={state.subdomain}
              onChange={(ev) =>
                setState((s) => ({ ...s, subdomain: ev.target.value }))
              }
              fullWidth
              helperText={lightMdToJsx(
                t('org.integrations.bambooHr.setupInfo.subdomainHelper')
              )}
            />
          </CustomInputLabel>
        </FormRow>
        <FormRow>
          <CustomInputLabel
            label={t('org.integrations.common.apiKey')}
            fullWidth
          >
            <TextField
              variant="outlined"
              value={state.apiKey}
              onChange={(ev) =>
                setState((s) => ({ ...s, apiKey: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeBambooHrConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
            disabled={!subdomain && !apiKey}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={() =>
                callFirebaseFunction(
                  'integrations_bamboohr-verifyCredentialsAndStore',
                  {
                    orgId: config.data.orgId,
                    subdomain: state.subdomain,
                    apiKey: state.apiKey
                  }
                ).then(onSuccessfulIntegration)
              }
              disabled={
                !state.subdomain ||
                !state.apiKey ||
                state.subdomain === config.data.subdomain ||
                state.apiKey === config.data.apiKey
              }
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

export const BambooHrSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useBambooHrConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      name={NAME}
      functionalities={functionalities}
      logoUrl="/images/integrations-logos/bamboohr.svg"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
